import { SENDA } from '@somnhold/content';
import { cellId } from '../grid.ts';
import { hexLen } from '../hex.ts';
import type { CamsRules, EmberRules, VentRules } from '../types.ts';
import type { CamsLayout, DeepLayout, EmberLayout } from './corazon-layout.ts';

/**
 * The heart's rules from their layouts (docs/sendas/artifice.md, chapter 3): the fight's builder (campaign/sendas.ts)
 * and the renderer's scenes build them the same way. SENDA.vents, ember and cams. La Tenaza's life and Dunna's bolt in
 * hit points (seconds of D, worked out by the caller). `radius`: the board's (the layout's + 2), for the cell ids.
 */

export const ventRules = (L: DeepLayout, dunnaHit: number): VentRules => {
  const V = SENDA.vents;
  return {
    way: L.way.map((p) => ({ x: p.x, y: p.y })),
    vents: L.vents.map((v) => ({ ...v })),
    holes: L.holeAt.map((h) => ({ ...h })),
    chalk: [...L.chalk],
    mouth: { ...L.mouth },
    warn: V.warn,
    blow: V.blow,
    heroBp: V.heroBp,
    blows: V.blows,
    foeBp: V.foeBp,
    pace: V.pace,
    wait: V.wait,
    hold: V.hold,
    reach: V.reach,
    foes: { ...V.foes },
    dunna: { every: V.dunna.every, hit: dunnaHit, range: V.dunna.range },
  };
};

export const emberRules = (L: EmberLayout, radius: number, dunnaHit: number): EmberRules => {
  const E = SENDA.ember;
  // (Never on Dunna's place, nor the cells round it.)
  const near = (h: { q: number; r: number }): boolean => hexLen(h.q, h.r + 2) <= 1;
  return {
    work: E.work,
    dunnaAt: { ...L.dunnaAt },
    floor: L.floor.filter((h) => !near(h)).map((h) => cellId(radius, h.q, h.r)),
    spawns: L.spawns.filter((p) => !L.tunnels.some((t) => t.x === p.x && t.y === p.y)).map((p) => ({ ...p })),
    tunnels: L.tunnels.map((p) => ({ ...p })),
    pulse: { ...E.pulse },
    foes: { ...E.foes },
    dunna: { every: E.dunna.every, hit: dunnaHit, range: E.dunna.range },
  };
};

export const camsRules = (L: CamsLayout, radius: number, bossHp: number, dunnaHit: number): CamsRules => {
  const C = SENDA.cams;
  const id = (h: { q: number; r: number }): number => cellId(radius, h.q, h.r);
  return {
    slots: L.slots.map(id),
    lie: L.lie.map(id),
    lever: id(L.lever),
    seq: [...L.seq],
    stand: C.stand,
    pull: C.pull,
    reach: C.reach,
    face: C.face,
    chips: C.chips,
    stone: { ...L.stone },
    wheel: { ...L.wheel },
    river: L.river0.map((p) => ({ ...p })),
    pulse: { ...C.pulse },
    foes: { ...C.foes },
    boss: { ...C.boss, splash: { ...C.boss.splash }, hp: bossHp, at: { ...L.bossAt } },
    dunnaAt: { ...L.dunnaAt },
    dunna: { every: C.dunna.every, hit: dunnaHit, range: C.dunna.range },
  };
};
